import React, { useState, useRef, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { UploadCloud, X, ChevronDown, Image } from "lucide-react";
import styles from "../../_css/ContenetManagement.module.css";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { notifyError, notifySuccess } from "../../utils/helper";
import { useDispatch } from "react-redux";
import { uploadVideoTutorials, updateTutorial, upload_thumbnail } from "../../requests.js";
import RichTextEditor from "../CMS/RichTextEditor.js";

const EditTutorialModal = ({ show, onHide, onSuccess, tutorialData }) => {
    const dispatch = useDispatch();
    const [uploadType, setUploadType] = useState("video");
    const [videoFile, setVideoFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [readOnlyFiles, setReadOnlyFiles] = useState([]);
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        shortSummary: "",
        duration: "",
        url: ""
    });
    const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
    const [thumbnailUploading, setThumbnailUploading] = useState(false);
    const [loading, setLoading] = useState(false);

    // Store initial data to preserve when no changes are made
    const [initialData, setInitialData] = useState(null);

    const contentTypeOptions = [
        { value: "video", label: "Video Tutorial" },
        { value: "readonly", label: "Read Only Document" }
    ];

    const handleRichTextChange = (html) => {
        setFormData(prev => ({ ...prev, description: html }));
    };

    useEffect(() => {
        if (show && tutorialData) {
            // Determine content type based on tutorial data
            const contentType = tutorialData.filterType === "video" ? "video" : "readonly";
            setUploadType(contentType);

            const initialFormData = {
                title: tutorialData.title || "",
                description: tutorialData.description || "",
                duration: tutorialData.duration || "",
                shortSummary: tutorialData.shortSummary || "",
                url: tutorialData.ctaLink || ""
            };

            setFormData(initialFormData);
            setInitialData({
                ...initialFormData,
                contentType,
                fileName: tutorialData.fileName,
                ctaLink: tutorialData.ctaLink,
                thumbnail: tutorialData.thumbnail,
                tags: tutorialData.tags,
                status: tutorialData.status
            });

            // If it's a video tutorial and has existing file info
            if (contentType === "video" && tutorialData.fileName) {
                setVideoFile({ name: tutorialData.fileName });
            }

            // If it's a document tutorial and has existing documents
            if (contentType === "readonly" && tutorialData.documents) {
                setReadOnlyFiles(tutorialData.documents.map(doc => ({ name: doc.name })));
            }
        }
    }, [show, tutorialData]);

    // Handle form input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleVideoFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validVideoTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/mkv', 'video/webm'];
            if (!validVideoTypes.includes(file.type)) {
                notifyError("Please upload a valid video file (MP4, MOV, AVI, MKV, WEBM)");
                return;
            }

            const maxSize = 50 * 1024 * 1024;
            if (file.size > maxSize) {
                notifyError("File size must be less than 50MB");
                return;
            }

            setVideoFile(file);
            setFormData(prev => ({ ...prev, url: "" }));
        }
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validImageTypes.includes(file.type)) {
                notifyError("Please upload a valid image file (JPEG, PNG, GIF, WEBP)");
                return;
            }

            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                notifyError("Thumbnail size must be less than 5MB");
                return;
            }

            setThumbnailFile(file);
        }
    };

    const handleReadOnlyFileChange = (e) => {
        const files = Array.from(e.target.files);
        setReadOnlyFiles(prevFiles => [...prevFiles, ...files]);
        setFormData(prev => ({ ...prev, url: "" }));
    };

    const removeVideoFile = () => {
        setVideoFile(null);
    };

    const removeThumbnail = () => {
        setThumbnailFile(null);
    };

    const removeReadOnlyFile = (index) => {
        setReadOnlyFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    const triggerThumbnailInput = () => document.getElementById('upload-thumbnail').click();

    // Validate form data
    const validateForm = () => {
        if (!formData.title.trim()) {
            notifyError("Please enter a title");
            return false;
        }

        if (uploadType === "readonly") {
            if (!formData.shortSummary.trim()) {
                notifyError("Please enter a short summary");
                return false;
            }
            if (!formData.description.trim()) {
                notifyError("Please enter a description");
                return false;
            }
        } else {
            // Video type validation
            if (!formData.description.trim()) {
                notifyError("Please enter a description");
                return false;
            }
            if (!videoFile && !formData.url && !initialData?.ctaLink) {
                notifyError("Please upload a video file or provide a URL");
                return false;
            }
            if (!formData.duration.trim()) {
                notifyError("Please enter video duration");
                return false;
            }
        }
        return true;
    };

    // Check if form has changes
    const hasFormChanges = () => {
        if (!initialData) return false;

        const videoChanged = uploadType === "video" && (
            (videoFile && videoFile.name !== initialData.fileName) || (!videoFile && initialData.fileName)
        );

        const docsChanged = uploadType === "readonly" && (
            readOnlyFiles.length > 0 && readOnlyFiles[0].name !== initialData.fileName
        );

        return (
            formData.title !== initialData.title ||
            formData.description !== initialData.description ||
            formData.shortSummary !== initialData.shortSummary ||
            formData.duration !== initialData.duration ||
            formData.url !== initialData.url ||
            videoChanged ||
            docsChanged ||
            thumbnailFile !== null
        );
    };
    const uploadThumbnail = async (file) => {
        try {
            setThumbnailUploading(true);
            setThumbnailUploadProgress(0);

            const thumbnailResponse = await upload_thumbnail(file);
            setThumbnailUploadProgress(100);

            return thumbnailResponse.blobUrl;
        } catch (error) {
            notifyError("Failed to upload thumbnail");
            throw error;
        } finally {
            setThumbnailUploading(false);
        }
    };

    // Handle video update
    // FIX 1: Accept targetStatus argument
    const handleVideoUpdate = async (targetStatus) => {
        try {
            let updatePayload = {
                id: tutorialData.id,
                title: formData.title,
                description: formData.description,
                duration: formData.duration,
                thumbnail: initialData?.thumbnail || "",
                filterType: "video",
                tags: initialData?.tags || ["Video Tutorial"],
                cta: "Watch Now",
                ctaLink: initialData?.ctaLink,
                // FIX 1: Use targetStatus if provided, otherwise fallback to published (safest for updates)
                status: targetStatus || "published",
                fileName: initialData?.fileName
            };

            // Upload new thumbnail if provided
            if (thumbnailFile) {
                try {
                    const thumbnailUrl = await uploadThumbnail(thumbnailFile);
                    updatePayload.thumbnail = thumbnailUrl;
                    notifySuccess("Thumbnail updated successfully!");
                } catch (thumbnailError) {
                    notifyError("Thumbnail upload failed, keeping existing thumbnail");
                }
            }

            // If new video file is uploaded
            if (videoFile && typeof videoFile !== 'string') {
                const videoResponse = await uploadVideoTutorials(videoFile);
                updatePayload.ctaLink = videoResponse.blobUrl;
                updatePayload.fileName = videoFile.name;
            }
            // If URL is provided and different from initial
            else if (formData.url && formData.url !== initialData?.url) {
                updatePayload.ctaLink = formData.url;
                updatePayload.fileName = null;
            }
            // If no file or URL changes, keep the initial data
            else if (!videoFile && !formData.url) {
                updatePayload.ctaLink = initialData?.ctaLink;
                updatePayload.fileName = initialData?.fileName;
            }

            const id = tutorialData.id;
            const response = await updateTutorial(id, updatePayload);
            return response;
        } catch (error) {
            throw error;
        }
    };


    // Handle read-only (document) update
    // FIX 1: Accept targetStatus argument
    const handleReadOnlyUpdate = async (targetStatus) => {
        try {
            const updatePayload = {
                id: tutorialData.id,
                title: formData.title,
                description: formData.description,
                shortSummary: formData.shortSummary,
                thumbnail: initialData?.thumbnail || "",
                filterType: "document",
                tags: initialData?.tags || ["Document Tutorial"],
                cta: "Read Now",
                ctaLink: initialData?.ctaLink || formData.url,
                // FIX 1: Use targetStatus if provided, otherwise fallback to published
                status: targetStatus || "published",
                fileName: initialData?.fileName
            };

            console.log("Read-only update payload:", updatePayload);

            // Upload new thumbnail if provided
            if (thumbnailFile) {
                try {
                    const thumbnailUrl = await uploadThumbnail(thumbnailFile);
                    updatePayload.thumbnail = thumbnailUrl;
                    notifySuccess("Thumbnail updated successfully!");
                } catch (thumbnailError) {
                    notifyError("Thumbnail upload failed, keeping existing thumbnail");
                }
            }

            console.log("Final update payload:", updatePayload);
            const id = tutorialData.id;
            const response = await updateTutorial(id, updatePayload);
            return response;
        } catch (error) {
            console.error("Error in handleReadOnlyUpdate:", error);
            throw error;
        }
    };

    // Handle update
    // FIX 2: Accept targetStatus to distinguish between 'Update' (keep status) and 'Publish Now' (force published)
    const handleUpdate = async (targetStatus) => {
        if (!validateForm()) return;

        // FIX 3: Check if status is actually changing
        const isStatusChanging = targetStatus && targetStatus !== initialData?.status;

        // Check if there are any changes
        // Only return early if NO form changes AND NO status changes
        if (!hasFormChanges() && !isStatusChanging) {
            notifySuccess("No changes detected. Tutorial remains unchanged.");
            onHide();
            return;
        }

        dispatch(startLoader());
        setLoading(true);
        try {
            let response;

            if (uploadType === "video") {
                response = await handleVideoUpdate(targetStatus);
            } else {
                response = await handleReadOnlyUpdate(targetStatus);
            }

            if (response?.success) {
                notifySuccess(`Tutorial ${targetStatus === 'published' ? 'published' : 'updated'} successfully!`);
                resetForm();
                onHide();
                if (onSuccess) onSuccess();
            } else {
                notifyError("Failed to update tutorial");
            }
        } catch (error) {
            notifyError(error.response?.data?.message || "Failed to update tutorial");
        } finally {
            dispatch(stopLoader());
            setLoading(false);
        }
    };

    // Handle save as draft (for published tutorials)
    const handleSaveAsDraft = async () => {
        if (!formData.title.trim()) {
            notifyError("Please enter a title to save as draft");
            return;
        }

        dispatch(startLoader());
        setLoading(true);
        try {
            const draftPayload = {
                id: tutorialData.id,
                title: formData.title,
                description: uploadType === "readonly" ? formData.description : formData.description,
                shortSummary: uploadType === "readonly" ? formData.shortSummary : undefined,
                thumbnail: initialData?.thumbnail || "",
                filterType: uploadType === "video" ? "video" : "document",
                tags: initialData?.tags || ["Draft"],
                cta: uploadType === "video" ? "Watch Now" : "Read Now",
                ctaLink: initialData?.ctaLink,
                status: "draft",
                fileName: initialData?.fileName
            };

            // Upload new thumbnail if provided
            if (thumbnailFile) {
                try {
                    const thumbnailUrl = await uploadThumbnail(thumbnailFile);
                    draftPayload.thumbnail = thumbnailUrl;
                } catch (thumbnailError) {
                    // Continue without thumbnail update if upload fails
                }
            }

            // Handle file updates if new files are selected
            if (uploadType === "video" && videoFile && typeof videoFile !== 'string') {
                const videoResponse = await uploadVideoTutorials(videoFile);
                draftPayload.ctaLink = videoResponse.blobUrl;
                draftPayload.fileName = videoFile.name;
            }
            // If URL is provided and different from initial
            else if (formData.url && formData.url !== initialData?.url) {
                draftPayload.ctaLink = formData.url;
                draftPayload.fileName = null;
            }
            // If no changes to file/URL, keep initial data
            else {
                draftPayload.ctaLink = initialData?.ctaLink;
                draftPayload.fileName = initialData?.fileName;
            }
            const id = tutorialData.id;
            const response = await updateTutorial(id, draftPayload);


            if (response.success) {
                notifySuccess("Tutorial saved as draft successfully!");
                resetForm();
                onHide();
                if (onSuccess) onSuccess();
            } else {
                notifyError("Failed to save as draft");
            }
        } catch (error) {
            notifyError(error.response?.data?.message || "Failed to save as draft");
        } finally {
            dispatch(stopLoader());
            setLoading(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: "",
            description: "",
            shortSummary: "",
            duration: "",
            url: ""
        });
        setVideoFile(null);
        setThumbnailFile(null);
        setReadOnlyFiles([]);
        setUploadType("video");
        setThumbnailUploadProgress(0);
        setThumbnailUploading(false);
        setInitialData(null);
    };

    // Reset form when modal closes
    useEffect(() => {
        if (!show) {
            resetForm();
        }
    }, [show]);

    const isFormDisabled = loading || thumbnailUploading;

    // Check if tutorial is currently a draft
    const isDraft = tutorialData?.status === "draft";

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            dialogClassName="modal-38"
        >
            <Modal.Body className={isFormDisabled ? styles.tutorialModal__disabled : ""}>
                <div className={styles.tutorialModal__container}>
                    {/* Header */}
                    <div className={styles.tutorialModal__header}>
                        <X
                            onClick={onHide}
                            className={styles.tutorialModal__closeButton}
                            size={28}
                        />
                        <span className={styles.tutorialModal__title}>
                            Edit Tutorial Content
                        </span>
                    </div>

                    {/* Upload Type Selection - Disabled for editing */}
                    <div className={styles.tutorialModal__formSection}>
                        <div className={styles.tutorialModal__flex1}>
                            <label className={styles.tutorialModal__label}>Content Type</label>
                            <div
                                className={styles.tutorialModal__select}
                                style={{
                                    opacity: 0.6,
                                    cursor: 'not-allowed',
                                    background: '#f9fafb'
                                }}
                            >
                                {uploadType === "video" ? "Video Tutorial" : "Read Only Document"}
                            </div>
                            <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                                Content type cannot be changed during editing
                            </small>
                        </div>
                    </div>

                    {/* Title */}
                    <div className={styles.tutorialModal__formSection}>
                        <div className={styles.tutorialModal__flex1}>
                            <label className={styles.tutorialModal__label}>Title *</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => handleInputChange("title", e.target.value)}
                                placeholder={uploadType === "video" ? "Ted Talk" : "Document Title"}
                                className={styles.tutorialModal__input}
                                disabled={isFormDisabled}
                            />
                        </div>
                    </div>

                    {/* Short Summary - Only for read-only content */}
                    {uploadType === "readonly" && (
                        <div className={styles.tutorialModal__formSection}>
                            <div className={styles.tutorialModal__flex1}>
                                <label className={styles.tutorialModal__label}>Short Summary *</label>
                                <input
                                    type="text"
                                    value={formData.shortSummary}
                                    onChange={(e) => handleInputChange("shortSummary", e.target.value)}
                                    placeholder="Enter a brief summary"
                                    className={styles.tutorialModal__input}
                                    disabled={isFormDisabled}
                                />
                            </div>
                        </div>
                    )}

                    {/* Description */}
                    {uploadType === "readonly" ? (
                        <div className={styles.tutorialModal__formSection}>
                            <label className={styles.tutorialModal__label}>Description *</label>
                            <RichTextEditor
                                value={formData.description}
                                onChange={handleRichTextChange}
                                label="Description"
                            />
                        </div>
                    ) : (
                        // Show normal video description input
                        <div className={styles.tutorialModal__formSection}>
                            <label className={styles.tutorialModal__label}>Description *</label>
                            <input
                                type="text"
                                value={formData.description}
                                onChange={(e) => handleInputChange("description", e.target.value)}
                                placeholder="Enter description"
                                className={styles.tutorialModal__input}
                                disabled={isFormDisabled}
                            />
                        </div>
                    )}

                    {/* Thumbnail Upload Section - Show for both video and read-only */}
                    <div className={styles.tutorialModal__formSection}>
                        <label className={styles.tutorialModal__label}>
                            Update Thumbnail {thumbnailUploading && "(Uploading...)"}
                            {initialData?.thumbnail && " (Current thumbnail is set)"}
                        </label>
                        <div className={styles.tutorialModal__thumbnailContainer}>
                            {thumbnailFile ? (
                                <div className={styles.tutorialModal__thumbnailPreview}>
                                    <img
                                        src={URL.createObjectURL(thumbnailFile)}
                                        alt="Thumbnail preview"
                                        className={styles.tutorialModal__thumbnailImage}
                                    />
                                    <div className={styles.tutorialModal__thumbnailActions}>
                                        <span className={styles.tutorialModal__fileName}>{thumbnailFile.name}</span>
                                        <X
                                            size={16}
                                            onClick={removeThumbnail}
                                            className={styles.tutorialModal__cursorPointer}
                                            disabled={isFormDisabled}
                                        />
                                    </div>
                                </div>
                            ) : initialData?.thumbnail ? (
                                <div className={styles.tutorialModal__thumbnailPreview}>
                                    <img
                                        src={initialData.thumbnail}
                                        alt="Current thumbnail"
                                        className={styles.tutorialModal__thumbnailImage}
                                    />
                                    <div className={styles.tutorialModal__thumbnailActions}>
                                        <span className={styles.tutorialModal__fileName}>Current thumbnail</span>
                                        <div
                                            className={`${styles.tutorialModal__uploadArea} ${styles.tutorialModal__uploadAreaSmall}`}
                                            onClick={isFormDisabled ? undefined : triggerThumbnailInput}
                                        >
                                            <Image size={16} color="#000000" />
                                            <span>Change</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={`${styles.tutorialModal__uploadArea} ${isFormDisabled ? styles.tutorialModal__uploadAreaDisabled : ''}`}
                                    onClick={isFormDisabled ? undefined : triggerThumbnailInput}
                                >
                                    <Image size={20} color="#000000" />
                                    <span className={styles.tutorialModal__uploadText}>Click to upload thumbnail</span>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailChange}
                                style={{ display: 'none' }}
                                id="upload-thumbnail"
                                disabled={isFormDisabled}
                            />
                        </div>
                        <p className={styles.tutorialModal__helpText}>
                            Recommended: 16:9 aspect ratio, max 5MB (JPEG, PNG, GIF, WEBP)
                        </p>

                        {thumbnailUploading && (
                            <div className={styles.tutorialModal__progressContainer}>
                                <div
                                    className={styles.tutorialModal__progressBar}
                                    style={{ width: `${thumbnailUploadProgress}%` }}
                                ></div>
                            </div>
                        )}
                    </div>

                    {/* Video Upload Section */}
                    {uploadType === "video" && (
                        <>
                            <div className={styles.tutorialModal__fileUploadSection}>
                                {/* Duration */}
                                <div className={styles.tutorialModal__flex1}>
                                    <label className={styles.tutorialModal__label}>Duration *</label>
                                    <input
                                        type="text"
                                        value={formData.duration}
                                        onChange={(e) => handleInputChange("duration", e.target.value)}
                                        placeholder="2min"
                                        className={styles.tutorialModal__input}
                                        disabled={isFormDisabled}
                                    />
                                </div>
                                {/* Upload Video */}
                                <div className={styles.tutorialModal__flex1}>
                                    <label className={styles.tutorialModal__label}>
                                        Upload Video {initialData?.fileName && `(Current: ${initialData.fileName})`}
                                    </label>
                                    <div
                                        className={`${styles.tutorialModal__input} ${styles.tutorialModal__fileDisplay} ${isFormDisabled ? styles.tutorialModal__uploadAreaDisabled : ''}`}
                                        onClick={isFormDisabled ? undefined : () => document.getElementById('upload-video').click()}
                                    >
                                        <span>
                                            {videoFile ? videoFile.name : "Choose new video file..."}
                                        </span>
                                        {videoFile && (
                                            <X
                                                size={16}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeVideoFile();
                                                }}
                                                className={styles.tutorialModal__cursorPointer}
                                            />
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        accept="video/*"
                                        onChange={handleVideoFileChange}
                                        className={styles.tutorialModal__hidden}
                                        id="upload-video"
                                        disabled={isFormDisabled}
                                    />
                                </div>
                            </div>

                            {/* URL Upload Section for Video */}
                            <div className={styles.tutorialModal__formSection}>
                                <label className={styles.tutorialModal__label}>
                                    or Update the URL {initialData?.ctaLink && "(Current URL is set)"}
                                </label>
                                <div className={styles.tutorialModal__urlContainer}>
                                    <input
                                        type="text"
                                        value={formData.url}
                                        onChange={(e) => handleInputChange("url", e.target.value)}
                                        placeholder="https://www.figma.com/design/ZNlHpXnXUSGCqVDeLxE0f4/WorkEwhere?node"
                                        className={styles.tutorialModal__urlInput}
                                        disabled={isFormDisabled || (uploadType === "video" ? !!videoFile : readOnlyFiles.length > 0)}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {/* Action Buttons */}
                    <div className={styles.tutorialModal__actionButtons}>
                        {isDraft ? (
                            <>
                                <button
                                    className={styles.tutorialModal__draftButton}
                                    onClick={handleSaveAsDraft}
                                    disabled={isFormDisabled}
                                >
                                    Update Draft
                                </button>
                                {/* FIX 4: Explicitly pass "published" status */}
                                <button
                                    className={styles.tutorialModal__publishButton}
                                    onClick={() => handleUpdate("published")}
                                    disabled={isFormDisabled}
                                >
                                    Publish Now
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className={styles.tutorialModal__draftButton}
                                    onClick={handleSaveAsDraft}
                                    disabled={isFormDisabled}
                                >
                                    Save As Draft
                                </button>
                                {/* FIX 4: Explicitly pass "published" status */}
                                <button
                                    className={styles.tutorialModal__publishButton}
                                    onClick={() => handleUpdate("published")}
                                    disabled={isFormDisabled}
                                >
                                    Update
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default EditTutorialModal;